/**
 * The «Come lavoriamo» trail (Method.tsx): a footpath across the four steps,
 * drawn as on a hiking map — dotted, meandering, climbing left to right from
 * the meadow to the woods — with a signpost at each step, the walker's
 * footprints along it, and the life around it: grass and flowers by the
 * path, firs and the house where it ends, birds in the sky, and a dog for
 * company (motion/useTrailWalk.ts walks it).
 *
 * Everything here is plain geometry in the SVG's own units (viewBox
 * 0 0 BOX.w BOX.h), computed once at module load from a seeded generator:
 * the prerendered page carries every mark, the hook only reveals and moves
 * them, and the numbers are testable without a DOM.
 *
 * The four columns of the list are COLUMN units wide (grid gap 0, right
 * padding instead), so each signpost sits just inside its column's left
 * edge, over the step's title.
 */

export interface Pt {
  readonly x: number
  readonly y: number
}

/** The SVG box: 1000 wide like the timeline's line, tall enough for sky, climb and woods. */
export const BOX = { w: 1000, h: 150 } as const

/** Column width in the box: four columns, no gap. */
export const COLUMN = BOX.w / 4

/** A signpost's foot is this far inside its column's left edge. */
export const SIGN_INSET = 11

/** The four waypoints, one per step, climbing from the first to the last. */
export const WAYPOINTS: readonly Pt[] = [
  { x: SIGN_INSET, y: 126 },
  { x: COLUMN + SIGN_INSET, y: 106 },
  { x: 2 * COLUMN + SIGN_INSET, y: 84 },
  { x: 3 * COLUMN + SIGN_INSET, y: 60 },
]

/** One cubic Bézier leg from the previous point to `to`. */
export interface Leg {
  readonly c1: Pt
  readonly c2: Pt
  readonly to: Pt
}

/**
 * The trail's legs: waypoint to waypoint, each a gentle S-bend (down a
 * little, then up to the next signpost), then a tail that walks on out of
 * the last step, up to the door of the house among the trees.
 */
export const LEGS: readonly Leg[] = [
  { c1: { x: 95, y: 134 }, c2: { x: 175, y: 96 }, to: WAYPOINTS[1] },
  { c1: { x: 345, y: 116 }, c2: { x: 430, y: 70 }, to: WAYPOINTS[2] },
  { c1: { x: 600, y: 94 }, c2: { x: 680, y: 46 }, to: WAYPOINTS[3] },
  { c1: { x: 825, y: 70 }, c2: { x: 880, y: 32 }, to: { x: 932, y: 46 } },
]

/** One decimal: enough for the markup, stable across engines. */
export const r = (n: number): number => Math.round(n * 10) / 10

/** The trail as a path `d`. */
export const TRAIL_D: string = [
  `M ${r(WAYPOINTS[0].x)} ${r(WAYPOINTS[0].y)}`,
  ...LEGS.map((l) => `C ${r(l.c1.x)} ${r(l.c1.y)}, ${r(l.c2.x)} ${r(l.c2.y)}, ${r(l.to.x)} ${r(l.to.y)}`),
].join(' ')

const SAMPLES_PER_LEG = 96

function bezier(p0: Pt, l: Leg, t: number): Pt {
  const u = 1 - t
  const a = u * u * u
  const b = 3 * u * u * t
  const c = 3 * u * t * t
  const d = t * t * t
  return {
    x: a * p0.x + b * l.c1.x + c * l.c2.x + d * l.to.x,
    y: a * p0.y + b * l.c1.y + c * l.c2.y + d * l.to.y,
  }
}

/** A sample along the trail: its position and the distance walked to reach it. */
interface Sample extends Pt {
  readonly at: number
}

/** The trail sampled leg by leg, arc length accumulated; `legEnds[i]` is the distance at the end of leg i. */
function sample(start: Pt, legs: readonly Leg[]): { samples: Sample[]; legEnds: number[] } {
  const samples: Sample[] = [{ ...start, at: 0 }]
  const legEnds: number[] = []
  let from = start
  let last = start
  let at = 0
  for (const leg of legs) {
    for (let i = 1; i <= SAMPLES_PER_LEG; i++) {
      const p = bezier(from, leg, i / SAMPLES_PER_LEG)
      at += Math.hypot(p.x - last.x, p.y - last.y)
      samples.push({ ...p, at })
      last = p
    }
    legEnds.push(at)
    from = leg.to
  }
  return { samples, legEnds }
}

const { samples: SAMPLES, legEnds: LEG_ENDS } = sample(WAYPOINTS[0], LEGS)

/** The whole trail's length, tail included. */
export const LENGTH: number = LEG_ENDS[LEG_ENDS.length - 1]

/** Where the trail is `s` units from its start, and which way it runs there (degrees, clockwise in SVG's y-down space). */
export function pointAt(s: number): { x: number; y: number; angle: number } {
  const clamped = Math.max(0, Math.min(LENGTH, s))
  let i = SAMPLES.findIndex((p) => p.at >= clamped)
  if (i <= 0) i = 1
  const a = SAMPLES[i - 1]
  const b = SAMPLES[i]
  const span = b.at - a.at
  const t = span > 0 ? (clamped - a.at) / span : 0
  const before = SAMPLES[Math.max(0, i - 2)]
  const after = SAMPLES[Math.min(SAMPLES.length - 1, i + 1)]
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    angle: (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI,
  }
}

/** A point `dist` units off the trail at `s`: side +1 is the walker's left — uphill, the sky side — and -1 the right, downhill. */
export function beside(s: number, side: 1 | -1, dist: number): Pt {
  const p = pointAt(s)
  const rad = (p.angle * Math.PI) / 180
  return { x: p.x + side * dist * Math.sin(rad), y: p.y - side * dist * Math.cos(rad) }
}

export interface Sign extends Pt {
  /** Distance along the trail where the walker reaches it. */
  readonly at: number
}

/** One signpost per waypoint: the first at the start, the others at the end of the leg that reaches them. */
export const SIGNS: readonly Sign[] = WAYPOINTS.map((w, i) => ({ x: w.x, y: w.y, at: i === 0 ? 0 : LEG_ENDS[i - 1] }))

/** Where the walk ends: the last signpost. */
export const END: number = SIGNS[SIGNS.length - 1].at

/** Distance between two footprints, heel to heel: a stride. */
export const STRIDE = 30
/** How far each foot lands off the trail's centre line, to its side. */
export const LATERAL = 4.5
/** No footprint this close to a signpost: the post stands on clear ground. */
export const SIGN_CLEAR = 15

/** Where the dog stops to sniff, as a share of the walk; the flower it sniffs stands just off the trail there. */
export const SNIFF_SHARE = 0.42
export const SNIFF_AT: number = r(SNIFF_SHARE * END)

export interface Print extends Pt {
  /** The trail's heading there, degrees: the print points along it. */
  readonly angle: number
  /** Distance along the trail. */
  readonly at: number
  /** Which foot: alternates, +1 then -1. */
  readonly side: 1 | -1
}

/** The last footprint stops this short of the trail's end: the walker is at the door. */
export const DOOR_CLEAR = 30

/**
 * The footprints, one stride apart from the first signpost up to the door
 * of the house, each set off the centre line to its foot's side, left then
 * right — and left out where a signpost stands or the sniffed flower
 * grows. The feet keep alternating across a gap, like the walker does.
 */
function walk(): Print[] {
  const out: Print[] = []
  let side: 1 | -1 = 1
  for (let s = STRIDE / 2; s <= LENGTH - DOOR_CLEAR; s += STRIDE) {
    const foot = side
    side = side === 1 ? -1 : 1
    if (SIGNS.some((c) => Math.abs(c.at - s) < SIGN_CLEAR)) continue
    if (Math.abs(SNIFF_AT - s) < 10) continue
    const p = beside(s, foot, LATERAL)
    out.push({ x: p.x, y: p.y, angle: pointAt(s).angle, at: s, side: foot })
  }
  return out
}

export const PRINTS: readonly Print[] = walk()

/* ------------------------------------------------------------------ life */

/** mulberry32: a tiny seeded generator, the same numbers in every engine. */
function rng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s + 0x6d2b79f5) >>> 0
    let t = s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export type PlantKind = 'tuft' | 'daisy' | 'bell' | 'fir'

export interface Plant extends Pt {
  readonly kind: PlantKind
  /** Distance along the trail the walker must reach for it to grow in. */
  readonly at: number
  readonly scale: number
  /** Seconds for one sway, and where in it the plant starts; firs do not sway. */
  readonly sway: number
  readonly phase: number
  /** The flower the dog stops to sniff. */
  readonly sniff?: true
}

/** No plant this close (along the trail) to a signpost: its board needs clear air. */
export const PLANT_SIGN_CLEAR = 32
/** Grass and flowers stand this far off the trail. */
export const PLANT_OFF_MIN = 12
export const PLANT_OFF_MAX = 20

/**
 * Grass and flowers every ~62 units of trail (jittered), alternating sides,
 * clear of the signposts and of the sniffed flower; then the sniffed flower
 * itself, right by the path on the downhill side; then three firs around
 * the house where the trail ends, the two big ones on the slope below it.
 */
function grow(): Plant[] {
  const rand = rng(20260925)
  const kinds: PlantKind[] = ['tuft', 'daisy', 'bell', 'tuft', 'daisy']
  const out: Plant[] = []
  let side: 1 | -1 = -1
  let k = 0
  for (let s = 42; s <= END - 42; s += 62) {
    const at = s + (rand() - 0.5) * 28
    side = side === 1 ? -1 : 1
    // Downhill plants grow up towards the path: they stand a little further off it.
    const off = PLANT_OFF_MIN + rand() * (PLANT_OFF_MAX - PLANT_OFF_MIN) + (side === -1 ? 4 : 0)
    const scale = 1 + rand() * 0.3
    const sway = 1.5 + rand() * 1.1
    const phase = rand() * 2
    const kind = kinds[k++ % kinds.length]
    if (SIGNS.some((c) => Math.abs(c.at - at) < PLANT_SIGN_CLEAR)) continue
    if (Math.abs(SNIFF_AT - at) < 30) continue
    const p = beside(at, side, off)
    if (p.y > BOX.h - 6) continue
    out.push({ kind, x: p.x, y: p.y, at, scale, sway, phase })
  }
  const sniff = beside(SNIFF_AT, -1, 6.5)
  out.push({ kind: 'daisy', x: sniff.x, y: sniff.y, at: SNIFF_AT, scale: 1.2, sway: 2.1, phase: 0.4, sniff: true })
  const firs: readonly [number, number, number][] = [
    [884, 106, 1.15],
    [940, 121, 0.95],
    [889, 21, 0.7],
  ]
  firs.forEach(([x, y, scale], i) => out.push({ kind: 'fir', x, y, at: END + 60 + i * 25, scale, sway: 0, phase: 0 }))
  return out
}

export const PLANTS: readonly Plant[] = grow()

export interface Bird extends Pt {
  readonly scale: number
}

/** Three birds over the meadow where the trail starts. */
export const BIRDS: readonly Bird[] = [
  { x: 118, y: 34, scale: 1.25 },
  { x: 154, y: 49, scale: 1.05 },
  { x: 194, y: 25, scale: 0.9 },
]

/** The house the trail leads to, just past its end — the client's home, where the walk arrives. Grown in right after the last print. */
export const HOUSE: Pt & { readonly at: number } = { x: 948, y: 46, at: END + 20 }

/** The dog's drawing scale on the trail, and where it sits down: at the end of the path, by the door. */
export const DOG_SCALE = 1
export const DOG_REST_AT: number = LENGTH - 20

/** The dog's `transform` on the trail at distance `s`: feet on the path, nose along it — or held at `tilt` degrees (0 to sit level). */
export function dogTransform(s: number, tilt?: number): string {
  const p = pointAt(s)
  return `translate(${r(p.x)} ${r(p.y - 1)}) rotate(${r(tilt ?? p.angle)}) scale(${DOG_SCALE})`
}
